import React, { useRef, useState, useCallback, useEffect, type ChangeEvent, type FormEvent } from "react";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { TINYMCE_API_KEY, TINYMCE_SETUP } from 'src/utils/tinymce';

import { IEventMessage } from "@eagerdog/interfaces";

import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";
import { apiService } from "src/services/api.service";
import { Modal, useModal } from "src/components/Modal/Modal";

import "./SendMessage.scss";
import Input from "../Input/Input";

interface IProps {
  handleSendMessage: (formData: IEventMessage) => void
  subject: string
  messageBody?: string
  topText?: JSX.Element
  type: SendMessageType
  isSending: boolean
  title?: string
  participantCount?: number
  onGetParticipantCount?: () => Promise<number>
  onConfirmationStateChange?: (isConfirming: boolean) => void
  eventId?: string
  clubId?: string
  recipientInfo?: {
    firstName: string
    lastName: string
    email: string
  }
  handlerEmail?: string
}

export enum SendMessageType {
  Registrants = "registrants",
  ShowRegistrants = "show_registrants",
  BulkEmailResults = "bulk_email_results",
  EmailResults = "email_results",
  ResendRegistration = "resend_registration",
  ClubContacts = "club_contacts",
  CourseRegistrants = "course_registrants",
}

const SendMessage: React.FC<IProps> = (props) => {
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [attachFinalConfirmation, setAttachFinalConfirmation] = useState<boolean>(false);
  const [addArmBands, setAddArmBands] = useState<boolean>(false);
  const [includeHandler, setIncludeHandler] = useState<boolean>(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [customEmailsField, setCustomEmailsField] = useState<string>("");
  const [customSubjectField, setCustomSubjectField] = useState<string>(props.subject);
  const [onlyQualified, setOnlyQualified] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [participantCount, setParticipantCount] = useState<number>(props.participantCount || 0);
  const [pendingPayload, setPendingPayload] = useState<IEventMessage | null>(null);
  
  // Modal for user selection
  const { isShown: isUserSelectionModalShown, toggle: toggleUserSelectionModal } = useModal();

  // User selection state for test sending
  const [testTargetUser, setTestTargetUser] = useState<{
    user_id: string;
    display_name: string;
    email: string;
  } | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<Array<{
    user_id: string;
    display_name: string;
    email: string;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce helper
  const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) & { cancel: () => void } => {
    let timeout: NodeJS.Timeout | null = null;
    
    const debounced = (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
    
    debounced.cancel = () => {
      if (timeout) clearTimeout(timeout);
    };
    
    return debounced;
  };

  // User search function
  const searchUsers = useCallback(
    debounce(async (query: string) => {
      if (!props.eventId) return;
      if (query.length < 2) {
        setUserSearchResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const results = await apiService.searchRegisteredUsersForTest(
          props.eventId,
          query
        );
        setUserSearchResults(results);
      } catch (error) {
        toast.show({
          title: "Search Error",
          content: "Failed to search users",
          duration: 5000,
          type: "fail"
        });
        setUserSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [props.eventId]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      searchUsers.cancel();
    };
  }, [searchUsers]);

  const handleAttachmentSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!validateAttachmentsSize(file)) return;
      setAttachedFiles([file, ...attachedFiles]);
      // NOTE: allows to select the same file after it has been removed for example
      // we dont use inputs values anyway
      event.target.value = "";
    }
  }

  const handleAttachmentRemove = (file: File) => {
    setAttachedFiles(attachedFiles.filter((attachment) => {
      return attachment.name !== file.name && attachment.lastModified !== file.lastModified;
    }));
  }

  // NOTE: check on current attachments + new input file
  const validateAttachmentsSize = (file: File): boolean => {
    const maxSize = 1e+7; // 10 MB
    const summary: number = attachedFiles.map((attachment) => attachment.size)
      .reduce((a, b) => a + b, file.size);
    if (summary > maxSize) {
      toast.show({
        title: "Attachments limit",
        content: "Overall files size should not exceed 10 megabytes",
        duration: 10000,
        type: "fail"
      });
      return false;
    }
    return true;
  }

  const truncateFileName = (file: File) => {
    const name = file.name;
    const maxLength = 14;
    if (name.length > maxLength) return name.slice(0, maxLength - 1) + "…";
    return name;
  }

  const MAX_SAFE_SIZE = 400000; // 400 KB safe threshold

  const checkPayloadSize = (message: IEventMessage): boolean => {
    const { content, attachments = [], attach_registration_summary, add_armbands } = message;

    // Estimate size of message content
    const messageSize = new Blob([content]).size;

    // Estimate size of attachments (base64 overhead = ~33%)
    const attachmentSize = attachments.reduce((total, file) => {
      return total + file.size * 1.33;
    }, 0);

    // Estimate generated PDF size for optional flags (rough approximation)
    let estimatedPdfSize = 0;
    if (attach_registration_summary) estimatedPdfSize += 150000; // ~150KB
    if (add_armbands) estimatedPdfSize += 100000; // ~100KB

    const totalSize = messageSize + attachmentSize + estimatedPdfSize;

    return totalSize <= MAX_SAFE_SIZE;
  };

  const handleSubmitSendMessage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(false)
  };

  const handleSubmitTestSendMessage = () => {
    // If eventId exists, show modal for user selection
    if (props.eventId) {
      toggleUserSelectionModal();
      return;
    }
    
    // For non-event contexts, just send the test
    handleSubmit(true);
  };

  const handleConfirmTestSend = () => {
    // Validate user selection is required for event context
    if (!testTargetUser) {
      toast.show({
        title: "Select Test User",
        content: "Please select a registered user to generate test content",
        duration: 5000,
        type: "fail"
      });
      return;
    }
    
    toggleUserSelectionModal();
    handleSubmit(true);
  };

  const handleSubmit = async (isTestSending?: boolean) => {
    if (!editorRef.current) return;
  
    const content = editorRef.current.getContent();
  
    if (!content) {
      toast.show({
        title: "Event Message",
        content: "Please type in a message",
        duration: 10000,
        type: "fail"
      });
      return;
    }

    const rawEmails = customEmailsField.split(',').map(email => email.trim());
    const customEmails = rawEmails.filter(email => !!email);
    if (includeHandler && props.handlerEmail) {
      customEmails.push(props.handlerEmail);
    }
    const payload: IEventMessage = {
      content,
      attach_registration_summary: attachFinalConfirmation,
      add_armbands: addArmBands,
      attachments: attachedFiles,
      custom_emails: customEmails,
      custom_subject: customSubjectField ?? undefined,
      is_test_sending: isTestSending,
      only_qualified: onlyQualified,
      test_target_user_id: isTestSending ? testTargetUser?.user_id : undefined,
    };

    // TODO: remove when rework attachments handle for all email types
    if (props.type !== SendMessageType.Registrants && props.type !== SendMessageType.ShowRegistrants && !checkPayloadSize(payload)) {
      toast.show({
        title: "Message too large",
        content: "Your message and attachments may be too large to send. Try shortening the content or removing files.",
        duration: 10000,
        type: "fail"
      });
      return;
    }

    // Show confirmation for both Registrants and ShowRegistrants (unless it's a test)
    if ((props.type === SendMessageType.ShowRegistrants || props.type === SendMessageType.Registrants) && !isTestSending) {
      try {
        let count = participantCount;
        if (props.onGetParticipantCount) {
          count = await props.onGetParticipantCount();
          setParticipantCount(count);
        }
        setPendingPayload(payload);
        setShowConfirmation(true);
        props.onConfirmationStateChange?.(true);
      } catch (error) {
        toast.show({
          title: "Error",
          content: "Unable to get participant count",
          duration: 10000,
          type: "fail"
        });
      }
      return;
    }
  
    props.handleSendMessage(payload);
  };

  const handleConfirmSend = () => {
    // Close confirmation modal immediately for better UX
    setShowConfirmation(false);
    setPendingPayload(null);
    props.onConfirmationStateChange?.(false);
    
    // Then trigger the send message
    if (pendingPayload) {
      props.handleSendMessage(pendingPayload);
    }
  };

  const handleCancelSend = () => {
    setShowConfirmation(false);
    setPendingPayload(null);
    props.onConfirmationStateChange?.(false);
  };

  if (showConfirmation) {
    return (
      <div className='SendMessage SendMessageConfirmation'>
        <div className="title">Confirm {props.title ?? 'Send Message'}</div>
        <div className="formWrap">
          <div className="confirmationMessage">
            <p>This message will be sent to <strong>{participantCount} participants</strong>. Are you sure?</p>
          </div>
          <div className="inputsWrap">
            <div className="actions">
              <div className="buttons confirmationButtons">
                <Button 
                  type="button" 
                  onClick={handleCancelSend}
                  disabled={props.isSending}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  className="submit" 
                  onClick={handleConfirmSend}
                  isLoading={props.isSending}
                >
                  Confirm & Send
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className='SendMessage' onSubmit={handleSubmitSendMessage}>
      <div className="title">{props.title ?? 'Send Message'}</div>
      <div className="formWrap">
        {props.topText}
        {props.recipientInfo && (
          <div className="recipientInfo">
            <label>Sending to:</label>
            <span className="recipientDetails">
              {props.recipientInfo.firstName}{props.recipientInfo.lastName ? ` ${props.recipientInfo.lastName}` : ''} ({props.recipientInfo.email})
            </span>
          </div>
        )}
        {[SendMessageType.BulkEmailResults, SendMessageType.Registrants, SendMessageType.ShowRegistrants].includes(props.type) && (
          <Input
            autoComplete="off"
            type="text"
            label="Add additional recipients (comma separated)"
            onChange={(e) => { setCustomEmailsField(e.target.value); }}
            defaultValue={customEmailsField}
            placeholder="users1@email.com, users2@email.com"
          />
        )}
        <Input
          autoComplete="off"
          type="text"
          label="Subject"
          onChange={(e) => { setCustomSubjectField(e.target.value); }}
          defaultValue={customSubjectField}
          placeholder={props.subject}
        />
        <Editor
          apiKey={TINYMCE_API_KEY}
          onInit={(_evt, editor) => editorRef.current = editor}
          init={{
            plugins: 'emoticons link lists searchreplace table visualblocks wordcount paste contextmenu',
            toolbar: 'undo redo | fontfamily fontsize | bold italic underline strikethrough | link image mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
            block_unsupported_drop: true,
            paste_data_images: false,
            paste_enable_default_filters: true,
            paste_auto_cleanup_on_paste: true,
            paste_remove_styles: false,
            paste_remove_styles_if_webkit: false,
            contextmenu: 'cut copy paste | link table',
            automatic_uploads: false,
            images_upload_handler: () => {
              return Promise.reject("Image uploads are disabled.");
            },
            file_picker_types: "",
            tinycomments_mode: 'embedded',
            tinycomments_author: 'Author name',
            menubar: false,
            branding: false,
            mergetags_list: [
              { value: 'First.Name', title: 'First Name' },
              { value: 'Email', title: 'Email' },
            ],
            setup: TINYMCE_SETUP,
          }}
          initialValue={props.messageBody ?? ""}
        />
        <div className="inputsWrap">
          <div className="checkWrap">
            {(props.type === SendMessageType.Registrants || props.type === SendMessageType.ResendRegistration) && (
              <>
                <Checkbox
                  id="attachFinalConfirmation"
                  label="Attach Registrants Final Confirmation to Email"
                  value={attachFinalConfirmation}
                  onChange={() => {
                    setAttachFinalConfirmation(!attachFinalConfirmation);
                  }}
                />
                <Checkbox
                  id="addArmBands"
                  label="Include Armbands"
                  value={addArmBands}
                  onChange={() => {
                    setAddArmBands(!addArmBands);
                  }}
                />
                {props.type === SendMessageType.ResendRegistration && props.handlerEmail && (
                  <Checkbox
                    id="includeHandler"
                    label={`Include handler (${props.handlerEmail})`}
                    value={includeHandler}
                    onChange={() => {
                      setIncludeHandler(!includeHandler);
                    }}
                  />
                )}
              </>
            )}
            {props.type === SendMessageType.BulkEmailResults && (
              <Checkbox
                id="onlyQualified"
                label="Only email Qualified results"
                value={onlyQualified}
                onChange={() => {
                  setOnlyQualified(!onlyQualified);
                }}
              />
            )}
          </div>
          <div className="actions">
            {props.type !== SendMessageType.CourseRegistrants && (
              <>
                <Button type="button" disabled={attachedFiles.length >= 3} onClick={(event) => {
                  event.stopPropagation();
                  uploadInputRef?.current?.click();
                }}>
                  Upload attachment
                  <input
                    ref={uploadInputRef}
                    style={{ display: 'none' }}
                    type='file'
                    accept='image/*, application/pdf'
                    onChange={handleAttachmentSelect}
                  />
                </Button>
                <div className="attachedFiles">
                  {attachedFiles.map((file, index) => (
                    <Button key={index} type="button" className="attachment">
                      {truncateFileName(file)}
                      <span className="icon" onClick={() => handleAttachmentRemove(file)}></span>
                    </Button>
                  ))}
                </div>
              </>
            )}
            <div className="buttons">
              {props.type !== SendMessageType.CourseRegistrants && (
                <Button
                  className="submit"
                  type="button"
                  onClick={handleSubmitTestSendMessage}
                  disabled={props.isSending}
                >
                  Send Test
                </Button>
              )}
              <Button type="submit" className="submit" isLoading={props.isSending}>{props.title ?? 'Send Message'}</Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* User Selection Modal for Test Sending */}
      <Modal
        isShown={isUserSelectionModalShown}
        hide={toggleUserSelectionModal}
        className="testUserSelectionModal"
        modalContent={
          <div className="testUserSelectionModalContent">
            <div className="title">Select Test User</div>
            <div className="formWrap">
              <p className="description">
                Choose a registered user whose data will be used to generate test attachments (registration summary, armbands, etc.)
              </p>
              
              <div className="testUserSelection">
                {!testTargetUser ? (
                  <>
                    <Input
                      autoComplete="off"
                      type="text"
                      label="Search for user: *"
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        searchUsers(e.target.value);
                      }}
                    />
                    {isSearching && <span className="searching">Searching...</span>}
                    
                    {/* Search Results Dropdown */}
                    {userSearchResults.length > 0 && userSearchQuery.length >= 2 && (
                      <ul className="searchResults">
                        {userSearchResults.map(user => (
                          <li
                            key={user.user_id}
                            onClick={() => {
                              setTestTargetUser(user);
                              setUserSearchQuery('');
                              setUserSearchResults([]);
                            }}
                          >
                            <div className="userInfo">
                              <div className="name">{user.display_name}</div>
                              <div className="email">{user.email}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {/* No Results Message */}
                    {userSearchQuery.length >= 2 && userSearchResults.length === 0 && !isSearching && (
                      <div className="noResults">No users found</div>
                    )}
                  </>
                ) : (
                  <>
                    <label>Selected user:</label>
                    <div className="selectedUser">
                      <div className="userInfo">
                        <span className="name">{testTargetUser.display_name}</span>
                        <span className="email">{testTargetUser.email}</span>
                      </div>
                      <button 
                        type="button" 
                        className="clearButton"
                        onClick={() => {
                          setTestTargetUser(null);
                          setUserSearchQuery('');
                        }}
                        aria-label="Clear selection"
                      >
                        ✕
                      </button>
                    </div>
                  </>
                )}
                
                <p className="helper">
                  {testTargetUser 
                    ? `✓ Test will use ${testTargetUser.display_name}'s registrations for attachments`
                    : "Start typing to search for a registered user"}
                </p>
              </div>

              <div className="modalActions">
                <Button 
                  type="button" 
                  onClick={toggleUserSelectionModal}
                  disabled={props.isSending}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  className="submit" 
                  onClick={handleConfirmTestSend}
                  isLoading={props.isSending}
                  disabled={!testTargetUser}
                >
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>
        }
      />
    </form>
  );
}

export default SendMessage;
